// «Улучшения» бокового меню Конструктора (README §4.5): блок под списком схем,
// над лимитами, относится к выбранной схеме. Группы Логика / Безопасность /
// Расход видны по режиму; строка рекомендации «▸ название · −520 · ✓ · ×»,
// подробности по клику, наведение подсвечивает элемент на полотне.

import { useEffect } from 'react'
import { Chevron } from '../../ui/Icon'
import { SideHeader, SideRow, useFold } from '../../ui/SideMenu'
import { fmtRate } from './geom'
import { useFlowStore } from './store'
import type { Mode, SuggCategory, Suggestion } from './types'
import './improve.css'

/** Группы по режиму (таблица §4.5). */
export const SUGG_GROUPS: { id: SuggCategory; name: string; empty: string; modes: Mode[] }[] = [
  { id: 'logic', name: 'Логика', empty: 'по логике замечаний нет', modes: ['structure', 'data', 'nodes'] },
  { id: 'security', name: 'Безопасность', empty: 'периметр в порядке', modes: ['structure', 'security'] },
  { id: 'cost', name: 'Расход', empty: 'расход схемы в норме', modes: ['structure', 'live'] },
]

/** Непрочитанные рекомендации выбранной схемы — бейдж лампочки в полосе 56. */
export function useSuggestionCount(): number {
  return useFlowStore((s) => (s.sugg?.items ?? []).filter((x) => !s.suggSeen[x.id]).length)
}

function Item({ a }: { a: Suggestion }) {
  const read = useFlowStore((s) => s.suggRead === a.id)
  const hover = useFlowStore((s) => s.suggHover === a.id)
  const busy = useFlowStore((s) => s.suggBusy)
  const st = useFlowStore.getState
  return (
    <div
      className={`fl-imp__item${hover ? ' is-hover' : ''}`}
      onPointerEnter={(e) => { if (e.pointerType !== 'touch') st().setSuggHover(a.id) }}
      onPointerLeave={() => st().setSuggHover(null)}
    >
      <div className="fl-imp__row">
        <button
          className="fl-imp__title"
          title="подробнее"
          aria-expanded={read}
          onClick={() => st().setSuggRead(read ? null : a.id)}
        >
          <Chevron open={read} className="fl-imp__chev" />
          <span className="fl-imp__name" title={a.title}>{a.title}</span>
        </button>
        {a.savingTokPerMin ? <span className="fl-imp__save" title="экономия, ток/мин">−{fmtRate(a.savingTokPerMin)}</span> : null}
        <button className="fl-imp__ok" title="согласовать" disabled={!!busy} onClick={() => void st().acceptSuggestion(a.id)}>✓</button>
        <button className="fl-imp__no" title="отклонить" disabled={busy === 'adapt'} onClick={() => void st().rejectSuggestion(a.id)}>×</button>
      </div>
      {read ? (
        <div className="fl-imp__body">
          <div className="fl-imp__full">{a.title}</div>
          <div className="fl-imp__changes">
            {a.changes.map((c, i) => (
              <div key={i}>
                <div className="fl-imp__what">{c.what}</div>
                <div className="fl-imp__fromto">
                  {c.same ? (
                    <>
                      {c.from ? <span className="fl-imp__from">{c.from}</span> : null}
                      <span className="fl-imp__arrow">·</span>
                      <span className="fl-imp__same">без изменений</span>
                    </>
                  ) : (
                    <>
                      <span className="fl-imp__from">{c.from}</span>
                      <span className="fl-imp__arrow">→</span>
                      <span className={c.fact ? 'fl-imp__fact' : 'fl-imp__to'}>{c.to}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div><div className="fl-imp__what">Зачем</div><div className="fl-imp__text">{a.why}</div></div>
          <div><div className="fl-imp__what">Чем рискуем</div><div className="fl-imp__text">{a.risk}</div></div>
        </div>
      ) : null}
    </div>
  )
}

function Group({ id, name, empty, items, extra }: { id: string; name: string; empty: string; items: Suggestion[]; extra?: string[] }) {
  const [folded, toggle] = useFold(`flow:improve-${id}`)
  return (
    <>
      <SideRow
        item={{ id, label: name, meta: String(items.length), metaTone: items.length ? 'warn' : 'dim' }}
        level={1}
        chevron={folded ? 'closed' : 'open'}
        onClick={toggle}
      />
      {folded ? null : (
        <div className="fl-imp__list">
          {items.map((a) => <Item key={a.id} a={a} />)}
          {!items.length ? <div className="fl-imp__empty">{empty}</div> : null}
          {extra?.map((t) => <div key={t} className="fl-imp__empty">{t}</div>)}
        </div>
      )}
    </>
  )
}

export function ImproveBlock() {
  const mode = useFlowStore((s) => s.mode)
  const sugg = useFlowStore((s) => s.sugg)
  const busy = useFlowStore((s) => s.suggBusy)
  const scheme = useFlowStore((s) => s.scheme)
  const [folded, toggle] = useFold('flow:improve')

  // блок раскрыт и виден — рекомендации прочитаны (гаснет бейдж лампочки)
  useEffect(() => {
    if (!folded && sugg) useFlowStore.getState().markSuggSeen()
  }, [folded, sugg])

  const items = sugg?.items ?? []
  const groups = SUGG_GROUPS.filter((g) => g.modes.includes(mode))
  const shown = items.filter((a) => groups.some((g) => g.id === a.category))
  const watching = (sugg?.applied ?? [])
    .filter((x) => x.op === 'engine' && x.verdict === 'watching')
    .map((x) => {
      const n = scheme?.nodes.find((m) => m.id === x.node)
      return `${n?.title ?? 'элемент'}: ${x.from} → ${x.to} · качество ${Math.min(x.turnsAfter, 20)}/20 ходов`
    })

  return (
    <div className="fl-imp">
      <SideHeader title="Улучшения" count={shown.length || undefined} open={!folded} onToggle={toggle} />
      {folded ? null : (
        <>
          {busy ? (
            <div className="fl-imp__status">
              {busy === 'recheck' ? 'перепроверяю…' : 'перестраиваю… новый движок подхватит следующий ход, текущий не прерывается'}
            </div>
          ) : null}
          {!sugg ? <div className="fl-imp__status">{scheme ? 'проверяю схему…' : 'нет схемы'}</div> : null}
          <div className="abx-side-rows" style={{ opacity: busy === 'recheck' ? 0.5 : 1 }}>
            {sugg ? groups.map((g) => (
              <Group
                key={g.id}
                id={g.id}
                name={g.name}
                empty={g.empty}
                items={items.filter((a) => a.category === g.id)}
                extra={g.id === 'cost' ? watching : undefined}
              />
            )) : null}
          </div>
        </>
      )}
    </div>
  )
}
